/**
 * X2 (CU-14zefhy1r83): polling of a CoPilot turn (`GET api/assistant/chat/events/{taskId}`) must survive long turns
 * (several minutes of tool calls). One failed poll (a gateway 502/504 or a slow PythonMagic read while a tool runs) used
 * to end the turn with "Lost connection to the assistant." after ~2 minutes. Now a turn keeps polling with no cap on the
 * turn itself; failed polls are retried with backoff and the turn gives up only after MAX_POLL_FAILURES network / 5xx
 * failures in a row, or at once when the task is gone (404 / 410). The API's retryable "still working, ask again"
 * answers (503 {retryable:true}, gateway 504) never count toward that limit; they are bounded by POLL_MAX_QUIET_MS
 * without a successful poll instead. A terminal task status (completed / failed / cancelled) ends it as before.
 *
 * T3 (assistant files wave): the retry limit is a time window, not a count. The assistant's host can restart for a few
 * minutes (PythonMagic) while the task keeps running, and the load balancers allow up to an hour, so network errors and
 * 5xx answers are retried with a growing pause (1.5 s up to 10 s) for POLL_LOST_AFTER_MS before the chat says
 * "Lost connection to the assistant."; meanwhile the reply bubble shows "Still working…" (never an error). 401 / 403
 * (not signed in / not allowed) and 404 / 410 (task gone) cannot be cured by waiting and end the turn at once.
 * Pure helpers so the rules can be unit tested without the component.
 */

import {ASSISTANT_LABEL} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-label';

/** pause between polls while the turn runs */
export const POLL_INTERVAL_MS = 1500;
/** how long the server may stay unreachable (network errors, 5xx) before the chat says the connection is lost */
export const POLL_LOST_AFTER_MS = 5 * 60 * 1000;
/** longest pause between two retries of a failed poll */
export const MAX_POLL_BACKOFF_MS = 10000;
/** how long the API may keep answering "still working, ask again" with no successful poll before the chat gives up */
export const POLL_MAX_QUIET_MS = 10 * 60 * 1000;
/** shortest pause after the API's retryable 503 (matches the proxy's Retry-After of 3 s) */
export const POLL_RETRYABLE_MIN_MS = 3000;
/** after this long without any narrative from the assistant, the reply bubble says it is still working */
export const STILL_WORKING_AFTER_MS = 20000;

export const LOST_CONNECTION_TEXT = `Lost connection to ${ASSISTANT_LABEL}.`;
export const STILL_WORKING_TEXT = 'Still working…';
export const RECONNECTING_TEXT = `Still working… reconnecting to ${ASSISTANT_LABEL}.`;

export interface PollFailureDecision {
    /** true: stop polling and fail the turn */
    giveUp: boolean;
    /** pause before the next poll when not giving up */
    delayMs: number;
}

/** The task no longer exists on the server (finished long ago, or the server restarted): retrying cannot help. */
export function isTaskGone(error: any): boolean {
    const status = error?.status;
    return status === 404 || status === 410;
}

/** The server refused the caller (401 not signed in, 403 not allowed): retrying cannot help. */
export function isPollRefused(error: any): boolean {
    const status = error?.status;
    return status === 401 || status === 403;
}

/** Pause before retry number `failures` (1-based): 1.5 s, 3 s, 6 s, then 10 s. */
export function pollBackoffMs(failures: number): number {
    const n = Math.min(Math.max(1, Math.floor(failures || 1)), 16); // bounded exponent; the wait itself is capped below
    return Math.min(MAX_POLL_BACKOFF_MS, POLL_INTERVAL_MS * Math.pow(2, n - 1));
}

/**
 * The API answered "the assistant is still working, ask again" rather than failing: its events proxy's 503 with
 * `{retryable: true}` in the body (PythonMagic held the task read past the proxy's timeout), or a gateway 504.
 * The body is read, not the Retry-After header: the API is called cross-origin and does not expose that header.
 */
export function isRetryablePollAnswer(error: any): boolean {
    const status = error?.status;
    return (status === 503 && error?.error?.retryable === true) || status === 504;
}

/** The wait the API asked for in the 503 body (`retryAfter`, seconds), in ms; 0 when absent. Never the header. */
function retryAfterMsOf(error: any): number {
    const seconds = Number(error?.error?.retryAfter);
    return isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
}

/**
 * What to do after a failed poll. `failures` = failed polls in a row (any kind) including this one; `quietMs` = time since
 * the last successful poll (or since the turn started); `unreachableMs` = time since the server last answered at all (a
 * successful poll or a retryable "still working" answer; defaults to `quietMs`). 404 / 410 / 401 / 403 end the turn at
 * once; retryable answers give up only after POLL_MAX_QUIET_MS; everything else (network error, other 5xx, 429) after
 * the server has been unreachable for POLL_LOST_AFTER_MS.
 */
export function onPollFailure(error: any, failures: number, quietMs = 0, unreachableMs = quietMs): PollFailureDecision {
    const retryable = isRetryablePollAnswer(error);
    if (isTaskGone(error) || isPollRefused(error) || (retryable ? quietMs >= POLL_MAX_QUIET_MS : unreachableMs >= POLL_LOST_AFTER_MS)) {
        return {giveUp: true, delayMs: 0};
    }
    const floor = Math.max(retryAfterMsOf(error), retryable && error?.status === 503 ? POLL_RETRYABLE_MIN_MS : 0);
    return {giveUp: false, delayMs: Math.min(MAX_POLL_BACKOFF_MS, Math.max(pollBackoffMs(failures), floor))};
}

/** True for a status that ends the turn; anything else (pending, running, unknown, empty) keeps polling. */
export function isTerminalTaskState(status: any): boolean {
    const state = String(status || '').toLowerCase();
    return state === 'completed' || state === 'failed' || state === 'cancelled';
}

/**
 * Text of the reply bubble while the turn runs and the assistant has sent no narrative yet: a reconnecting note while
 * polls fail, "Still working…" once the turn has run for a while, otherwise '' (only the typing dots show).
 */
export function waitingText(elapsedMs: number, failures: number): string {
    if (failures > 0) {
        return RECONNECTING_TEXT;
    }
    return elapsedMs >= STILL_WORKING_AFTER_MS ? STILL_WORKING_TEXT : '';
}

/**
 * The live line shown under the typing dots while a reply is still pending ("Still working…", the reconnecting note or
 * the assistant's latest narrative); '' once the reply has arrived (the bubble then shows the reply itself) or when
 * there is nothing to say yet.
 */
export function liveStatusText(message: {pending?: boolean; text?: string} | null | undefined): string {
    return message && message.pending ? (message.text || '').trim() : '';
}
